import { StrictMode, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { Cron } from "croner";
import { toString as cronToString } from "cronstrue";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Clipboard,
  Cloud,
  Code2,
  Download,
  Globe2,
  Info,
  RotateCcw,
  Server,
  Sparkles,
  TimerReset,
  Zap
} from "lucide-react";
import "./styles.css";

type Platform = {
  name: string;
  cadence: string;
  timezone: string;
  note: string;
  example: string;
};

type RunRow = {
  index: number;
  utc: string;
  primary: string;
  local: string;
  offsets: Record<string, string>;
};

type Warning = {
  level: "warn" | "info" | "good";
  title: string;
  body: string;
};

const presets = [
  { label: "Every 5 min", value: "*/5 * * * *" },
  { label: "Weekday 09:00", value: "0 9 * * 1-5" },
  { label: "Month close", value: "30 23 L * *" },
  { label: "Sunday report", value: "0 8 * * 0" },
  { label: "Quarter hour", value: "0,15,30,45 * * * *" }
];

const timezoneOptions = [
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Seoul",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney"
];

const platformNotes: Platform[] = [
  {
    name: "GitHub Actions",
    cadence: "UTC cron",
    timezone: "UTC only",
    note: "Scheduled workflows use UTC. Convert local business hours before copying the expression.",
    example: "on:\n  schedule:\n    - cron: \"0 14 * * 1-5\""
  },
  {
    name: "Cloudflare Workers",
    cadence: "Cron Triggers",
    timezone: "UTC schedule",
    note: "Cron triggers are configured in UTC. Use this preview to document customer-local time.",
    example: "\"triggers\": { \"crons\": [\"0 14 * * 1-5\"] }"
  },
  {
    name: "Vercel Cron",
    cadence: "UTC cron",
    timezone: "UTC only",
    note: "Cron jobs in project config run on UTC cadence. Keep human-facing reports explicit.",
    example: "{ \"crons\": [{ \"path\": \"/api/job\", \"schedule\": \"0 14 * * 1-5\" }] }"
  },
  {
    name: "Linux crontab",
    cadence: "Host local",
    timezone: "Server timezone",
    note: "Classic crontab follows the host timezone unless CRON_TZ or service configuration overrides it.",
    example: "CRON_TZ=America/New_York\n0 9 * * 1-5 /usr/local/bin/report"
  },
  {
    name: "Kubernetes CronJob",
    cadence: "Cluster API",
    timezone: "timeZone field",
    note: "Modern Kubernetes supports .spec.timeZone. Confirm your cluster version before relying on it.",
    example: "spec:\n  schedule: \"0 9 * * 1-5\"\n  timeZone: \"America/New_York\""
  }
];

const comparisonZones = ["UTC", "America/New_York", "Europe/London", "Asia/Seoul"];

function formatDate(date: Date, timezone: string, withZone = true) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: withZone ? "short" : undefined
  });
  return formatter.format(date);
}

function offsetLabel(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
    hour: "2-digit"
  }).formatToParts(date);
  return parts.find((part) => part.type === "timeZoneName")?.value ?? "UTC";
}

function generateRuns(expression: string, timezone: string, zones: string[], start: string): RunRow[] {
  const seed = start ? new Date(start) : new Date();
  const startDate = Number.isNaN(seed.getTime()) ? new Date() : seed;
  const schedule = new Cron(expression, { timezone, paused: true, mode: "5-or-6-parts" });
  return schedule.nextRuns(16, startDate).map((date, index) => ({
    index: index + 1,
    utc: formatDate(date, "UTC"),
    primary: formatDate(date, timezone),
    local: formatDate(date, Intl.DateTimeFormat().resolvedOptions().timeZone),
    offsets: Object.fromEntries(zones.map((zone) => [zone, `${formatDate(date, zone, false)} ${offsetLabel(date, zone)}`]))
  }));
}

function detectWarnings(rows: RunRow[], timezone: string, zones: string[], expression: string): Warning[] {
  const warnings: Warning[] = [];
  const minuteField = expression.trim().split(/\s+/)[0] ?? "";
  const allZones = [timezone, ...zones].filter((zone, index, list) => list.indexOf(zone) === index);

  allZones.forEach((zone) => {
    const offsets = rows.map((row) => row.offsets[zone] ?? row.primary).map((value) => value.match(/GMT[+-]\d+(?::\d+)?|UTC/)?.[0] ?? value);
    const changed = offsets.some((value, index) => index > 0 && value !== offsets[index - 1]);
    if (changed) {
      warnings.push({
        level: "warn",
        title: `${zone} offset changes inside this preview`,
        body: "At least one upcoming run crosses a daylight saving or timezone offset boundary. Confirm user-facing local time expectations before shipping."
      });
    }
  });

  if (minuteField === "*" || minuteField.includes("/1")) {
    warnings.push({
      level: "warn",
      title: "Very frequent schedule",
      body: "This can run every minute. Check platform quotas, cold starts, and idempotency before using it in production."
    });
  }

  if (warnings.length === 0) {
    warnings.push({
      level: "good",
      title: "No offset shift in the visible window",
      body: "The next 16 runs keep the same visible timezone offset across the selected comparison zones."
    });
  }

  warnings.push({
    level: "info",
    title: "Platform timezone behavior differs",
    body: "Cloud platforms often evaluate cron in UTC. Linux and Kubernetes may be timezone-aware depending on configuration."
  });

  return warnings.slice(0, 4);
}

function App() {
  const [expression, setExpression] = useState("0 9 * * 1-5");
  const [timezone, setTimezone] = useState("America/New_York");
  const [zones, setZones] = useState(comparisonZones);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 16));
  const [copied, setCopied] = useState("");

  const result = useMemo(() => {
    try {
      const description = cronToString(expression, { verbose: true });
      const rows = generateRuns(expression, timezone, zones, start);
      return {
        ok: true as const,
        description,
        rows,
        warnings: detectWarnings(rows, timezone, zones, expression),
        error: ""
      };
    } catch (error) {
      return {
        ok: false as const,
        description: "",
        rows: [],
        warnings: [],
        error: error instanceof Error ? error.message : "This cron expression could not be parsed."
      };
    }
  }, [expression, start, timezone, zones]);

  const report = useMemo(() => {
    if (!result.ok) return "";
    return [
      `TimeZoneCron Lab report`,
      `Cron: ${expression}`,
      `Meaning: ${result.description}`,
      `Primary timezone: ${timezone}`,
      `Preview start: ${start || "now"}`,
      "",
      "Next runs:",
      ...result.rows.slice(0, 10).map((row) => `${row.index}. ${row.primary} / ${row.utc}`),
      "",
      "Warnings:",
      ...result.warnings.map((warning) => `- ${warning.title}: ${warning.body}`)
    ].join("\n");
  }, [expression, result, start, timezone]);

  async function copyText(text: string, label: string) {
    await navigator.clipboard.writeText(text);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1400);
  }

  function toggleZone(zone: string) {
    setZones((current) => {
      if (current.includes(zone)) return current.filter((item) => item !== zone);
      return [...current, zone].slice(0, 5);
    });
  }

  const tableZones = zones.filter((zone) => zone !== timezone && zone !== "UTC").slice(0, 3);

  return (
    <main className="app-shell">
      <header className="topbar">
        <a data-google-vignette="false" className="brand" href="/">
          <span><CalendarClock size={23} /></span>
          <strong>TimeZoneCron Lab</strong>
        </a>
        <nav aria-label="Primary">
          <a data-google-vignette="false" href="#timeline">Timeline</a>
          <a data-google-vignette="false" href="#warnings">DST</a>
          <a data-google-vignette="false" href="#platforms">Platforms</a>
        </nav>
        <a data-google-vignette="false" className="utility-link" href="/privacy.html">Privacy</a>
      </header>

      <section className="command-grid" aria-label="Cron timezone workbench">
        <div className="command-panel">
          <div className="command-heading">
            <p>Cron timezone debugger</p>
            <h1>Preview every scheduled run before the job goes live.</h1>
          </div>

          <label className="cron-field">
            <span>Cron expression</span>
            <div>
              <Code2 size={20} />
              <input value={expression} onChange={(event) => setExpression(event.target.value)} spellCheck={false} />
              <button type="button" onClick={() => setExpression("0 9 * * 1-5")} title="Reset expression">
                <RotateCcw size={18} />
              </button>
            </div>
          </label>

          <div className="preset-row" aria-label="Cron presets">
            {presets.map((preset) => (
              <button key={preset.label} type="button" onClick={() => setExpression(preset.value)}>
                {preset.label}
              </button>
            ))}
          </div>

          <div className="control-grid">
            <label>
              <span>Primary timezone</span>
              <select value={timezone} onChange={(event) => setTimezone(event.target.value)}>
                {timezoneOptions.map((zone) => <option key={zone}>{zone}</option>)}
              </select>
            </label>
            <label>
              <span>Preview after</span>
              <input type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} />
            </label>
          </div>
        </div>

        <aside className="readout-panel">
          <div className={`status-card ${result.ok ? "ok" : "bad"}`}>
            {result.ok ? <CheckCircle2 size={22} /> : <AlertTriangle size={22} />}
            <div>
              <span>Status</span>
              <strong>{result.ok ? "Parsable schedule" : "Needs review"}</strong>
            </div>
          </div>
          <p className="description-text">{result.ok ? result.description : result.error}</p>
          <div className="zone-picker">
            <span>Compare zones</span>
            {timezoneOptions.map((zone) => (
              <button
                key={zone}
                type="button"
                className={zones.includes(zone) ? "selected" : ""}
                onClick={() => toggleZone(zone)}
              >
                {zone.replace("_", " ")}
              </button>
            ))}
          </div>
        </aside>
      </section>

      <section className="summary-strip" aria-label="Schedule summary">
        <Metric icon={<TimerReset size={20} />} label="Next run" value={result.ok ? result.rows[0]?.primary ?? "-" : "-"} />
        <Metric icon={<Globe2 size={20} />} label="Primary zone" value={timezone} />
        <Metric icon={<Zap size={20} />} label="Preview depth" value={result.ok ? `${result.rows.length} runs` : "0 runs"} />
        <Metric icon={<AlertTriangle size={20} />} label="DST checks" value={result.ok ? `${result.warnings.filter((warning) => warning.level === "warn").length} warnings` : "Blocked"} />
      </section>

      <section className="main-grid">
        <section className="timeline-panel" id="timeline">
          <div className="panel-heading">
            <div>
              <span>Run timeline</span>
              <h2>Next scheduled executions</h2>
            </div>
            <button type="button" onClick={() => copyText(report, "report")} disabled={!result.ok}>
              <Clipboard size={17} />
              {copied === "report" ? "Copied" : "Copy report"}
            </button>
          </div>

          {result.ok ? (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{timezone}</th>
                    <th>UTC</th>
                    {tableZones.map((zone) => <th key={zone}>{zone}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.index}>
                      <td>{row.index}</td>
                      <td><strong>{row.primary}</strong></td>
                      <td>{row.utc}</td>
                      {tableZones.map((zone) => <td key={zone}>{row.offsets[zone]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <AlertTriangle size={28} />
              <p>{result.error}</p>
            </div>
          )}
        </section>

        <aside className="side-stack">
          <section className="warning-panel" id="warnings">
            <div className="panel-heading compact">
              <div>
                <span>DST and risk queue</span>
                <h2>Review before deploy</h2>
              </div>
            </div>
            <div className="warning-list">
              {result.ok ? result.warnings.map((warning) => (
                <article key={warning.title} className={`warning-card ${warning.level}`}>
                  {warning.level === "good" ? <CheckCircle2 size={18} /> : warning.level === "info" ? <Info size={18} /> : <AlertTriangle size={18} />}
                  <div>
                    <strong>{warning.title}</strong>
                    <p>{warning.body}</p>
                  </div>
                </article>
              )) : (
                <article className="warning-card warn">
                  <AlertTriangle size={18} />
                  <div>
                    <strong>Fix syntax first</strong>
                    <p>The timeline and DST checks run after the expression can be parsed.</p>
                  </div>
                </article>
              )}
            </div>
          </section>

          <section className="export-panel">
            <div className="panel-heading compact">
              <div>
                <span>Copy-ready</span>
                <h2>Schedule evidence</h2>
              </div>
            </div>
            <pre>{report || result.error}</pre>
            <button type="button" onClick={() => copyText(report, "evidence")} disabled={!result.ok}>
              <Download size={17} />
              {copied === "evidence" ? "Copied" : "Copy evidence"}
            </button>
          </section>
        </aside>
      </section>

      <section className="platform-panel" id="platforms">
        <div className="section-title">
          <span><Server size={18} /> Platform timezone notes</span>
          <p>Copying a cron expression between tools is not enough. Confirm whether the platform evaluates in UTC, host local time, or a configured timezone.</p>
        </div>
        <div className="platform-grid">
          {platformNotes.map((platform) => (
            <article key={platform.name} className="platform-card">
              <div>
                <Cloud size={18} />
                <strong>{platform.name}</strong>
              </div>
              <span>{platform.cadence}</span>
              <p>{platform.note}</p>
              <pre>{platform.example}</pre>
            </article>
          ))}
        </div>
      </section>

      <section className="seo-panel">
        <div>
          <Sparkles size={20} />
          <h2>Cron expression next-run checker with timezone context</h2>
        </div>
        <p>
          Use TimeZoneCron Lab to explain cron expressions, compare upcoming run times across UTC and regional
          timezones, and document daylight saving time behavior before production schedules run.
        </p>
        <a data-google-vignette="false" href="#timeline">Debug a schedule <ChevronRight size={16} /></a>
      </section>

      <footer>
        <span>Independent cron preview utility. Validate production schedules against your provider documentation.</span>
        <nav>
          <a data-google-vignette="false" href="/privacy.html">Privacy</a>
          <a data-google-vignette="false" href="/about.html">About</a>
        </nav>
      </footer>
    </main>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <article className="metric-card">
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    </article>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
